import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@pray/shared/utils/styles';

import type { SegmentedControlProps } from './types';

const SIZE_CLASSES = {
  small: 'h-7 px-3 text-xs',
  medium: 'h-8 px-4 text-sm',
};

/**
 * Segmented control: a contained switcher where exactly one segment is active,
 * marked by a raised thumb that slides between segments.
 *
 * Use it for view switching (same content, different lens). Use underline tabs
 * for navigation and Chips for non-exclusive filters.
 */
export const SegmentedControl: React.FC<SegmentedControlProps> = ({
  options,
  value,
  onChange,
  size = 'medium',
  fullWidth = false,
  className,
  'aria-label': ariaLabel,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);

  const updateThumb = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const active = container.querySelector<HTMLElement>(`[data-segment-value="${CSS.escape(value)}"]`);
    if (!active) {
      setThumb(null);
      return;
    }
    setThumb({ left: active.offsetLeft, width: active.offsetWidth });
  }, [value]);

  useLayoutEffect(() => {
    updateThumb();
  }, [updateThumb, options, size, fullWidth]);

  // Keep the thumb aligned when the container resizes (responsive layouts, font load)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const observer = new ResizeObserver(() => updateThumb());
    observer.observe(container);

    return () => observer.disconnect();
  }, [updateThumb]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();

    const enabled = options.filter((o) => !o.disabled);
    if (enabled.length === 0) return;

    const currentIndex = enabled.findIndex((o) => o.value === value);
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const next = enabled[(currentIndex + delta + enabled.length) % enabled.length];

    onChange(next.value);
    containerRef.current?.querySelector<HTMLElement>(`[data-segment-value="${CSS.escape(next.value)}"]`)?.focus();
  };

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'relative inline-flex items-center rounded-full bg-theme-bg-secondary p-1 dark:bg-theme-bg-secondary/50 border border-theme-border-divider',
        fullWidth && 'flex w-full',
        className
      )}
    >
      {/* Sliding thumb */}
      {thumb && (
        <div
          aria-hidden="true"
          className="absolute inset-y-1 z-0 rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-[left,width] duration-200 ease-out motion-reduce:transition-none dark:bg-white/15 dark:ring-white/10"
          style={{ left: thumb.left, width: thumb.width }}
        />
      )}

      {options.map((option) => {
        const isActive = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={option.disabled}
            data-segment-value={option.value}
            tabIndex={isActive ? 0 : -1}
            className={cn(
              'relative z-10 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-semibold transition-colors duration-150',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400',
              SIZE_CLASSES[size],
              fullWidth && 'flex-1',
              isActive
                ? 'font-semibold text-gray-900 dark:text-white'
                : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
              option.disabled && 'cursor-not-allowed opacity-40'
            )}
            onClick={() => !option.disabled && onChange(option.value)}
            onKeyDown={handleKeyDown}
          >
            {option.icon}
            {option.label}
            {typeof option.count === 'number' && (
              <span
                className={cn(
                  'rounded-full px-1.5 py-px text-[10px] font-semibold tabular-nums',
                  isActive ? 'bg-black/5 dark:bg-white/15' : 'bg-black/5 text-gray-500 dark:bg-white/10'
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
